export const DEFAULT_ACCENT = '#8869eb';

const ACCENT_PRESETS = [
  { id: 'purple', hex: '#8869eb' },
  { id: 'blue', hex: '#3b82f6' },
  { id: 'green', hex: '#22c55e' },
  { id: 'teal', hex: '#14b8a6' },
  { id: 'orange', hex: '#f97316' },
  { id: 'red', hex: '#ef4444' },
  { id: 'pink', hex: '#ec4899' },
  { id: 'white', hex: '#f4f4f5' },
  { id: 'dark',  hex: '#45444b' },
];

export function getAccentPresets() {
  return ACCENT_PRESETS;
}

/**
 * @param {string} hex
 * @returns {string | null}
 */
export function normalizeAccentHex(hex) {
  if (typeof hex !== 'string') return null;
  const trimmed = hex.trim();
  const match = trimmed.match(/^#?([0-9a-fA-F]{6})$/);
  if (!match) return null;
  return `#${match[1].toLowerCase()}`;
}

function parseHex(hex) {
  const n = normalizeAccentHex(hex);
  if (!n) return null;
  return {
    r: parseInt(n.slice(1, 3), 16),
    g: parseInt(n.slice(3, 5), 16),
    b: parseInt(n.slice(5, 7), 16),
  };
}

/**
 * @param {{ r: number, g: number, b: number }} rgb
 * @param {number} amount 0–1 darken amount
 */
function darkenRgb({ r, g, b }, amount = 0.15) {
  const f = (c) => Math.max(0, Math.min(255, Math.round(c * (1 - amount))));
  return { r: f(r), g: f(g), b: f(b) };
}

/** @param {{ r: number, g: number, b: number }} rgb */
function lightenRgb({ r, g, b }, amount = 0.45) {
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + (255 - c) * amount)));
  return { r: f(r), g: f(g), b: f(b) };
}

function rgbToHex({ r, g, b }) {
  const h = (n) => n.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/**
 * @param {string} [hex]
 */
export function applyAccentToDocument(hex, fgOverride = 'auto') {
  if (typeof document === 'undefined') return DEFAULT_ACCENT;
  const normalized = normalizeAccentHex(hex) ?? DEFAULT_ACCENT;
  const rgb = parseHex(normalized);
  if (!rgb) return DEFAULT_ACCENT;

  const hover = rgbToHex(darkenRgb(rgb, 0.15));
  const light = rgbToHex(lightenRgb(rgb, 0.55));
  const soft = rgbToHex(lightenRgb(rgb, 0.35));
  let fg;
  if (fgOverride === 'light') fg = '#f4f4f5';
  else if (fgOverride === 'dark') fg = '#1a1825';
  else {
    const perceived = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    fg = perceived > 0.6 ? '#1a1825' : '#ffffff';
  }
  const el = document.documentElement;
  el.style.setProperty('--accent', normalized);
  el.style.setProperty('--accent-hover', hover);
  el.style.setProperty('--accent-light', light);
  el.style.setProperty('--accent-soft', soft);
  el.style.setProperty('--accent-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
  el.style.setProperty('--accent-muted', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.35)`);
  el.style.setProperty('--accent-fg', fg);
  el.style.setProperty(
    '--tw-gradient-from',
    normalized
  );
  el.style.setProperty(
    '--tw-gradient-to',
    `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`
  );

  return normalized;
}

export function isPresetAccent(hex) {
  const n = normalizeAccentHex(hex);
  if (!n) return false;
  return ACCENT_PRESETS.some((p) => p.hex === n);
}
